import React from 'react';
import ReactSelect from '@components/atoms/ReactSelect';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { SelectOption } from '@components/atoms/ReactSelect';
import { Task } from '@models/task.model';
import { useTranslation } from '@hooks/useTranslation';

interface StatusLockInfo {
  isDisabled: boolean;
  message: string | null;
}

interface StatusFieldProps {
  statusOptions: SelectOption[];
  values: any;
  task?: Task | null;
  errors: any;
  touched: any;
  statusUpdating: boolean;
  statusLoading: boolean;
  statusLockInfo: StatusLockInfo;
  onStatusChange: (status: string) => void;
}

export const StatusField: React.FC<StatusFieldProps> = ({
  statusOptions,
  values,
  task,
  errors,
  touched,
  statusUpdating,
  statusLoading,
  statusLockInfo,
  onStatusChange
}) => {
  const tTaskDetail = useTranslation('taskDetail');

  return (
    <FormGroup
      label={tTaskDetail.general.statusLabel}
      htmlFor="status"
      error={errors.status && touched.status ? errors.status : undefined}>
      <div className="relative">
        <ReactSelect
          key={`status-${task?.taskId || 'new'}-${values.status}`}
          options={statusOptions}
          value={statusOptions.find(
            (option) => option.value === values.status,
          )}
          onChange={(option: any) =>
            onStatusChange(option?.value || '')
          }
          placeholder={tTaskDetail.general.selectStatus}
          isDisabled={
            statusUpdating || statusLoading || statusLockInfo.isDisabled
          }
        />
        {(statusUpdating || statusLoading) && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
          </div>
        )}
      </div>
      {task?.taskId && !statusLockInfo.message && (
        <p className="text-xs text-gray-500 mt-1">
          {tTaskDetail.general.statusChangesAppliedImmediately}
        </p>
      )}
      {statusLockInfo.message && (
        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-4 w-4 text-amber-400 mt-0.5"
                viewBox="0 0 20 20"
                fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-2">
              <p className="text-xs text-amber-800">
                <strong>{tTaskDetail.general.statusLocked}</strong> {statusLockInfo.message}
              </p>
            </div>
          </div>
        </div>
      )}
      {!statusLockInfo.message &&
        task?.originalTaskId &&
        statusOptions.length < 4 && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg
                  className="h-4 w-4 text-blue-400 mt-0.5"
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-2">
                <p className="text-xs text-blue-800">
                  <strong>{tTaskDetail.general.delegatedTaskLabel}</strong>{' '}
                  {tTaskDetail.general.statusOptionsLimitedMessage}
                </p>
              </div>
            </div>
          </div>
        )}
    </FormGroup>
  );
};